// src/redux/slices/coachSlice.js
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import client from '../../apollo/client';
import { GET_COACH_DASHBOARD, VIEW_ATHLETE, GET_COACH_ATHLETES_WITH_ASSESSMENTS } from '../../apollo/queries/coach';

export const fetchCoachDashboard = createAsyncThunk(
  'coach/fetchDashboard',
  async (_, thunkAPI) => {
    try {
      const { data } = await client.query({
        query: GET_COACH_DASHBOARD,
        fetchPolicy: 'no-cache'
      });
      return data.getCoachDashboard;
    } catch (err) {
      console.error("❌ Error fetching coach dashboard:", err);
      return thunkAPI.rejectWithValue(err.message);
    }
  }
);

export const fetchSelectedAthlete = createAsyncThunk(
  'coach/fetchSelectedAthlete',
  async (athleteId, thunkAPI) => {
    try {
      const { data } = await client.query({
        query: VIEW_ATHLETE,
        variables: { athleteId },
        fetchPolicy: 'no-cache'
      });
      return data.viewAthlete;
    } catch (err) {
      console.error("❌ Error fetching selected athlete:", err);
      return thunkAPI.rejectWithValue(err.message);
    }
  }
);

export const fetchCoachAthletesWithAssessments = createAsyncThunk(
  'coach/fetchCoachAthletesWithAssessments',
  async (sport, thunkAPI) => {
    try {
      const { data } = await client.query({
        query: GET_COACH_ATHLETES_WITH_ASSESSMENTS,
        variables: { sport },
        fetchPolicy: 'no-cache',
      });
      return data.getCoachAthletesWithAssessments;
    } catch (err) {
      console.error("❌ Error fetching athletes with assessments:", err);
      return thunkAPI.rejectWithValue(err.message);
    }
  }
);

const coachSlice = createSlice({
  name: 'coach',
  initialState: {
    profile: null,
    athletes: [],
    assessments: [],
    athletesWithAssessments: [],
    selectedAthlete: null,
    selectedAthleteAssessments: [],
    loading: false,
    loadingLeaderboard: false,
    error: null,
  },
  reducers: {
    clearCoachState(state) {
      state.profile = null;
      state.athletes = [];
      state.assessments = [];
      state.selectedAthlete = null;
      state.selectedAthleteAssessments = [];
      state.loading = false;
      state.error = null;
    },
    clearSelectedAthlete(state) {
      state.selectedAthlete = null;
      state.selectedAthleteAssessments = [];
    },
    addAssessment(state, action) {
      state.assessments.unshift(action.payload);
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchCoachDashboard.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCoachDashboard.fulfilled, (state, action) => {
        state.loading = false;
        state.profile = action.payload.coach;
        state.athletes = action.payload.athletes;
        state.assessments = action.payload.assessments;
      })
      .addCase(fetchCoachDashboard.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // handle selected athlete
      .addCase(fetchSelectedAthlete.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchSelectedAthlete.fulfilled, (state, action) => {
        state.loading = false;
        state.selectedAthlete = action.payload.athlete;
        state.selectedAthleteAssessments = action.payload.assessments;
      })
      .addCase(fetchSelectedAthlete.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // handle athletes leaderboard
      .addCase(fetchCoachAthletesWithAssessments.pending, (state) => {
        state.loadingLeaderboard = true;
        state.error = null;
      })
      .addCase(fetchCoachAthletesWithAssessments.fulfilled, (state, action) => {
        state.loadingLeaderboard = false;
        state.athletesWithAssessments = action.payload;
        // Keep athletes list in sync so MyAthletesPanel always reflects current connections
        state.athletes = action.payload.map(item => item.athlete);
      })
      .addCase(fetchCoachAthletesWithAssessments.rejected, (state, action) => {
        state.loadingLeaderboard = false;
        state.error = action.payload;
      });
  },
});

export const { clearCoachState, clearSelectedAthlete, addAssessment } = coachSlice.actions;
export default coachSlice.reducer;